import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Instagram,
  Mail,
  Link as LinkIcon,
  ShoppingCart,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Inbox,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import './Attribution.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/attribution');


const getToken = () => localStorage.getItem('access_token') || '';


const STAGE_ICON_MAP = {
  reel: Instagram,
  post: Instagram,
  view: Instagram,
  dm: Mail,
  flow: Mail,
  link: LinkIcon,
  click: LinkIcon,
  checkout: ShoppingCart,
  cart: ShoppingCart,
  purchase: CheckCircle2,
  order: CheckCircle2,
};


const getStageIcon = (label = '') => {
  const key = label.toLowerCase();
  const match = Object.keys(STAGE_ICON_MAP).find((k) => key.includes(k));
  return STAGE_ICON_MAP[match] || LinkIcon;
};


const STAGE_COLORS = ['blue', 'purple', 'cyan', 'orange', 'emerald'];


const Attribution = () => {
  const [funnels, setFunnels] = useState([]);
  const [attributionSteps, setAttributionSteps] = useState([]);
  const [charts, setCharts] = useState([]);
  const [selectedFunnelIdx, setSelectedFunnelIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchAttribution = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });


      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }


      const data = await res.json();
      setFunnels(Array.isArray(data?.funnels) ? data.funnels : []);
      setAttributionSteps(Array.isArray(data?.attribution_steps) ? data.attribution_steps : []);
      setCharts(Array.isArray(data?.charts) ? data.charts : []);
    } catch (err) {
      console.error('Attribution fetch error:', err);
      setError('Unable to load attribution data from the server.');
      setFunnels([]);
      setAttributionSteps([]);
      setCharts([]);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchAttribution();
  }, [fetchAttribution]);


  // Note: attribution_steps and charts are now top-level/global in the API response,
  // so selecting a funnel tab no longer changes the displayed steps or charts.
  // The tab switcher is kept for forward-compatibility if the API later scopes
  // attribution_steps/charts per funnel.
  const hasSteps = attributionSteps.length > 0;
  const hasCharts = charts.length > 0;


  return (
    <>
      <Helmet>
        <title>Attribution Map - Revexia AI Revenue Brain</title>
        <meta name="description" content="Revenue attribution flow and conversion funnel" />
      </Helmet>


      <div className="attribution-page-wrapper">
        <div className="container-fluid px-3 px-md-4">
          <div className="attribution-header">
            <div className="attribution-header-left">
              <h1 className="attribution-page-title">Revenue Attribution Map</h1>
              <p className="attribution-page-subtitle">
                Connect every purchase to the originating post, DM flow, offer, and session.
              </p>
            </div>
            <button
              className="attribution-refresh-btn"
              onClick={fetchAttribution}
              disabled={loading}
              aria-label="Refresh attribution data"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>


          {error && !loading && (
            <div className="fallback-notice">
              <Inbox size={14} />
              <span>{error}</span>
            </div>
          )}


          {funnels.length > 1 && (
            <div className="funnel-tabs">
              {funnels.map((f, idx) => (
                <button
                  key={f.id || idx}
                  className={`funnel-tab-btn ${selectedFunnelIdx === idx ? 'active' : ''}`}
                  onClick={() => setSelectedFunnelIdx(idx)}
                >
                  {f.name || `Funnel ${idx + 1}`}
                </button>
              ))}
            </div>
          )}


          {loading ? (
            <div className="attribution-state-card">
              <div className="loading-spinner"></div>
              <p>Loading attribution data...</p>
            </div>
          ) : (
            <>
              {/* Customer Journey Attribution — driven by top-level attribution_steps */}
              {/* <section className="journey-card">
                <h3 className="journey-card-title">Customer Journey Attribution</h3>


                {hasSteps ? (
                  <div className="journey-steps-wrapper">
                    {attributionSteps.map((step, index) => {
                      const Icon = getStageIcon(step.label || step.name);
                      const color = STAGE_COLORS[index % STAGE_COLORS.length];
                      const dropValue = step.drop;
                      const hasDrop = dropValue !== null && dropValue !== undefined;
                      return (
                        <React.Fragment key={index}>
                          <div className={`journey-step journey-step-${color}`}>
                            <div className="journey-step-icon">
                              <Icon size={20} />
                            </div>
                            <p className="journey-step-label">{step.label || step.name}</p>
                            <p className={`journey-step-value text-${color}`}>
                              {step.val ?? step.value ?? step.count ?? '—'}
                              {step.unit ? ` ${step.unit}` : ''}
                            </p>
                          </div>
                          {index < attributionSteps.length - 1 && (
                            <div className="journey-connector">
                              <ArrowRight size={18} />
                              {hasDrop && (
                                <span
                                  className="journey-connector-drop"
                                  style={{ fontSize: '11px', opacity: 0.6, display: 'block', marginTop: '4px' }}
                                >
                                  {dropValue}% Drop
                                </span>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-data-block">
                    <Inbox size={26} />
                    <p>No journey data available</p>
                  </div>
                )}
              </section> */}


              {/* Diagnostic Charts — dynamically rendered from the API's charts array
                  (e.g. Revenue per Post, Messages per Weekday, Conversion, Purchase Duration) */}
              <div className="attribution-bottom-grid">
                {hasCharts ? (
                  charts.map((chart, idx) => (
                    <div className="attribution-panel trend-panel" key={idx}>
                      <h3 className="attribution-panel-title">{chart.title || `Chart ${idx + 1}`}</h3>
                      {chart.subtitle && (
                        <p className="attribution-panel-subtitle">{chart.subtitle}</p>
                      )}
                      <div className="trend-chart-body">
                        {Array.isArray(chart.data) && chart.data.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chart.data}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="rgba(255,255,255,0.06)"
                              />
                              <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'rgba(13, 15, 41, 0.95)',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  borderRadius: '12px',
                                }}
                                itemStyle={{ color: '#fff' }}
                              />
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke={chart.color || '#10B981'}
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: chart.color || '#10B981' }}
                                activeDot={{ r: 6 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="no-data-block">
                            <Inbox size={26} />
                            <p>No data available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="attribution-panel trend-panel">
                    <h3 className="attribution-panel-title">Attribution Charts</h3>
                    <div className="no-data-block">
                      <Inbox size={26} />
                      <p>No chart data available</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};


export default Attribution;